<template>
  <BaseModal
    v-model="showModal"
    title="Encaissement"
    size="5xl"
    :closable="!isProcessing"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white shadow-lg">
          <i class="fa-solid fa-money-bill"></i>
        </div>
        <div>
          <h2 class="text-xl font-semibold text-gray-900">Encaissement de la vente</h2>
          <p class="text-sm text-gray-500">{{ sale.invoice_number }}</p>
        </div>
      </div>
    </template>

    <div class="space-y-6">
      <!-- Message d'erreur -->
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <i class="fa-solid fa-circle-exclamation text-red-500 mt-0.5"></i>
        <span class="text-sm text-red-700">{{ errorMessage }}</span>
      </div>

      <!-- Résumé de la vente -->
      <div class="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Client</span>
          <span class="text-sm font-semibold text-gray-900">{{ customer?.name || 'Client anonyme' }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Date de vente</span>
          <span class="text-sm font-medium text-gray-700">{{ formatDate(sale.sale_date) }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Montant total</span>
          <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Déjà payé</span>
          <span class="text-sm font-semibold text-green-600">{{ formatCurrency(paidAmount) }}</span>
        </div>
        <div class="flex justify-between items-center pt-3 mt-2 bg-primary/10 -mx-4 -mb-4 px-4 pb-4 rounded-b-xl">
          <span class="text-sm font-medium text-gray-700">Reste à payer</span>
          <span class="text-lg font-bold text-primary">{{ formatCurrency(remainingAmount) }}</span>
        </div>
      </div>

      <!-- Montant à payer -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="dollar-sign" :size="18" />
          <span>Montant du paiement</span>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Montant à payer <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                v-model.number="paymentData.amount"
                type="number"
                :max="remainingAmount"
                min="1"
                step="1"
                class="w-full px-4 py-3 pr-16 text-base border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all"
                :class="{ 'border-red-500': validationErrors.amount }"
                :placeholder="`Maximum: ${formatCurrency(remainingAmount)}`"
              />
              <span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">FCFA</span>
            </div>
            <span v-if="validationErrors.amount" class="text-xs text-red-500 mt-1 block">{{ validationErrors.amount }}</span>
            <button
              type="button"
              class="w-full mt-2 py-2 px-4 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary hover:text-white transition-all"
              @click="paymentData.amount = remainingAmount"
            >
              Payer le solde complet ({{ formatCurrency(remainingAmount) }})
            </button>
          </div>
        </div>
      </div>

      <!-- Méthode de paiement -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="credit-card" :size="18" />
          <span>Mode de paiement <span class="text-red-500">*</span></span>
        </div>

        <div class="grid grid-cols-3 gap-3">
          <button
            v-for="method in availablePaymentMethods"
            :key="method.id"
            type="button"
            :class="[
              'payment-method-btn',
              { 'active': paymentData.payment_type === method.id },
              { 'wallet': method.id === 'wallet' }
            ]"
            @click="selectPaymentMethod(method.id)"
            :disabled="method.id === 'wallet' && !canPayWithWallet"
          >
            <i :class="['method-icon', method.icon]"></i>
            <span class="method-name">{{ method.name }}</span>
            <span v-if="method.id === 'wallet' && customerWallet" class="wallet-balance">
              {{ formatCurrency(customerWallet.balance) }}
            </span>
            <span v-if="method.id === 'wallet' && !canPayWithWallet" class="wallet-insufficient">
              Solde insuffisant
            </span>
          </button>
        </div>
        <span v-if="validationErrors.payment_type" class="text-xs text-red-500 mt-2 block">{{ validationErrors.payment_type }}</span>
      </div>

      <!-- Date et notes -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Date du paiement</label>
          <input
            type="datetime-local"
            v-model="paymentData.payment_date"
            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Notes (optionnel)</label>
          <input
            v-model="paymentData.notes"
            type="text"
            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all"
            placeholder="Référence, détails..."
            maxlength="500"
          />
        </div>
      </div>

      <!-- Récapitulatif -->
      <div v-if="paymentData.amount > 0 && paymentData.payment_type" class="bg-green-50 rounded-xl p-4 border border-green-200">
        <h4 class="text-sm font-semibold text-green-800 mb-3">Récapitulatif du paiement</h4>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span class="text-sm text-green-700">Montant</span>
            <span class="text-base font-bold text-green-600">{{ formatCurrency(paymentData.amount) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-green-700">Mode</span>
            <span class="text-sm font-medium text-green-800">{{ getMethodName(paymentData.payment_type) }}</span>
          </div>
          <div v-if="paymentData.amount < remainingAmount" class="flex justify-between items-center pt-2 border-t border-green-200">
            <span class="text-sm text-green-700">Reste après paiement</span>
            <span class="text-sm font-medium text-orange-600">{{ formatCurrency(remainingAmount - paymentData.amount) }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <span class="text-xs text-gray-500">* Champs obligatoires</span>
        <div class="flex gap-3">
          <BaseButton variant="ghost" :disabled="isProcessing" @click="closeModal">
            Annuler
          </BaseButton>
          <BaseButton
            variant="primary"
            :loading="isProcessing"
            :disabled="!canProcess"
            @click="processPayment"
          >
            <template #icon-left>
              <BaseIcon name="check" :size="18" />
            </template>
            Confirmer le paiement
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import paymentService from '@/services/paymentService';
import customerService from '@/services/customerService';
import CashRegisterService from '@/services/cashRegisterService';
import { useToastStore } from '@/store/modules/toastStore';
import { BaseModal, BaseButton, BaseIcon } from '@/components/base';

export default defineComponent({
  name: 'PaymentModal',
  components: {
    BaseModal,
    BaseButton,
    BaseIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    sale: {
      type: Object,
      required: true
    },
    customer: {
      type: Object,
      required: false
    },
    wallet: {
      type: Object,
      required: false
    }
  },
  emits: ['close', 'payment-success'],
  setup(props, { emit }) {
    const toastStore = useToastStore();
    const isProcessing = ref(false);
    const errorMessage = ref('');
    const validationErrors = ref({});
    const customerWallet = ref(null);
    const walletLoading = ref(false);

    const paymentData = ref({
      sale_id: '',
      amount: 0,
      payment_type: '',
      payment_date: '',
      cash_register_id: '',
      notes: ''
    });

    const paymentMethods = [
      { id: 'cash', name: 'Espèces', icon: 'fa-solid fa-money-bill' },
      { id: 'card', name: 'Carte bancaire', icon: 'fa-solid fa-credit-card' },
      { id: 'wave', name: 'Wave', icon: 'fa-solid fa-mobile' },
      { id: 'orange_money', name: 'Orange Money', icon: 'fa-solid fa-mobile' },
      { id: 'mobile_money', name: 'Mobile Money', icon: 'fa-solid fa-mobile' },
      { id: 'bank_transfer', name: 'Virement', icon: 'fa-solid fa-bank' },
      { id: 'check', name: 'Chèque', icon: 'fa-solid fa-file-invoice-dollar' },
      { id: 'wallet', name: 'Portefeuille', icon: 'fa-solid fa-wallet' }
    ];

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close');
        }
      }
    });

    const paidAmount = computed(() => {
      // Calculate from payments array if available
      if (props.sale.payments && props.sale.payments.length > 0) {
        return props.sale.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      }
      return props.sale.paid_amount || 0;
    });

    const remainingAmount = computed(() => {
      return (props.sale.total_incl_tax || 0) - paidAmount.value;
    });

    const canPayWithWallet = computed(() => {
      if (!customerWallet.value) return false;
      return customerWallet.value.balance >= paymentData.value.amount && paymentData.value.amount > 0;
    });

    const availablePaymentMethods = computed(() => {
        console.clear();
        console.log('props.wallet in availablePaymentMethods', props.customer);
      // Show wallet only if customer has a wallet
      if (props.customer && props.wallet) {
        return paymentMethods;
      }
      return paymentMethods.filter(m => m.id !== 'wallet');
    });

    const canProcess = computed(() => {
      return paymentData.value.payment_type &&
        paymentData.value.amount > 0 &&
        paymentData.value.amount <= remainingAmount.value &&
        props.sale.id;
    });

    const loadCustomerWallet = async () => {
      if (!props.wallet) {
        customerWallet.value = null;
        return;
      }

      try {
        walletLoading.value = true;
        const response = await customerService.getCustomerWallet(props.wallet.customer_id);
        console.log('customer wallet response', response.data.data);
        customerWallet.value = response.data.data;
      } catch (error) {
        console.error('Erreur lors du chargement du portefeuille:', error);
        customerWallet.value = null;
      } finally {
        walletLoading.value = false;
      }
    };

    const selectPaymentMethod = (methodId) => {
      if (methodId === 'wallet' && !canPayWithWallet.value) return;
      paymentData.value.payment_type = methodId;
      validationErrors.value.payment_type = '';
    };

    const getMethodName = (methodId) => {
      const method = paymentMethods.find(m => m.id === methodId);
      return method ? method.name : '';
    };

    const initializePaymentData = () => {
      const now = new Date();
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      paymentData.value = {
        sale_id: props.sale.id,
        amount: remainingAmount.value,
        payment_type: '',
        payment_date: localDateTime,
        cash_register_id: '',
        notes: ''
      };
      errorMessage.value = '';
      validationErrors.value = {};

      // Load customer wallet if customer exists
      loadCustomerWallet();
    };

    const validateForm = () => {
      validationErrors.value = {};
      let isValid = true;

      if (!paymentData.value.amount || paymentData.value.amount <= 0) {
        validationErrors.value.amount = 'Le montant doit être supérieur à 0';
        isValid = false;
      } else if (paymentData.value.amount > remainingAmount.value) {
        validationErrors.value.amount = `Le montant ne peut pas dépasser ${formatCurrency(remainingAmount.value)}`;
        isValid = false;
      }

      if (!paymentData.value.payment_type) {
        validationErrors.value.payment_type = 'Veuillez sélectionner un mode de paiement';
        isValid = false;
      }

      if (paymentData.value.payment_type === 'wallet' && customerWallet.value) {
        if (customerWallet.value.balance < paymentData.value.amount) {
          validationErrors.value.payment_type = 'Solde du portefeuille insuffisant';
          isValid = false;
        }
      }

      return isValid;
    };

    const processPayment = async () => {
      // Double-submit protection
      if (isProcessing.value) return;
      isProcessing.value = true;

      if (!validateForm()) {
        isProcessing.value = false;
        return;
      }

      // Get cash register only if not wallet payment
      if (paymentData.value.payment_type !== 'wallet') {
        try {
          const cashRegister = await CashRegisterService.getMyOpenedCashRegister();
          paymentData.value.cash_register_id = cashRegister.data.id;
        } catch (error) {
          console.error('Erreur lors de la récupération de la caisse:', error);
          errorMessage.value = error.message || 'Aucune caisse ouverte trouvée';
          isProcessing.value = false;
          return;
        }
      }

      try {
        errorMessage.value = '';

        const requestData = {
          sale_id: paymentData.value.sale_id,
          amount: paymentData.value.amount,
          payment_type: paymentData.value.payment_type,
          payment_date: paymentData.value.payment_date || undefined,
          notes: paymentData.value.notes || undefined
        };

        // Add cash_register_id only if not wallet payment
        if (paymentData.value.payment_type !== 'wallet') {
          requestData.cash_register_id = paymentData.value.cash_register_id;
        }

        await paymentService.makePayment(requestData);

        toastStore.addToast({
          type: 'success',
          message: 'Paiement enregistré avec succès',
          duration: 4000
        });

        emit('payment-success');
      } catch (error) {
        console.error('Erreur lors du paiement:', error);
        errorMessage.value = error.response?.data?.message || 'Erreur lors du paiement';
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du paiement',
          duration: 4000
        });
        isProcessing.value = false;
      }
    };

    const closeModal = () => {
      emit('close');
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    watch(() => props.show, (newVal) => {
      if (newVal) {
        initializePaymentData();
      }
    });

    // Update wallet validation when amount changes
    watch(() => paymentData.value.amount, () => {
      if (paymentData.value.payment_type === 'wallet' && !canPayWithWallet.value) {
        paymentData.value.payment_type = '';
      }
    });

    return {
      showModal,
      isProcessing,
      paymentData,
      errorMessage,
      validationErrors,
      customerWallet,
      walletLoading,
      paymentMethods,
      availablePaymentMethods,
      paidAmount,
      remainingAmount,
      canPayWithWallet,
      canProcess,
      selectPaymentMethod,
      getMethodName,
      processPayment,
      closeModal,
      formatCurrency,
      formatDate
    };
  }
});
</script>

<style scoped>
.payment-method-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.payment-method-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.payment-method-btn.active {
  border-color: #0A5C6B;
  background: #f0fdfa;
}

.payment-method-btn.wallet.active {
  border-color: #7c3aed;
  background: #f5f3ff;
}

.payment-method-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.method-icon {
  font-size: 1.5rem;
  color: #6b7280;
}

.payment-method-btn.active .method-icon {
  color: #0A5C6B;
}

.payment-method-btn.wallet.active .method-icon {
  color: #7c3aed;
}

.method-name {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  text-align: center;
}

.wallet-balance {
  font-size: 0.65rem;
  font-weight: 600;
  color: #059669;
  background: #d1fae5;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  margin-top: 0.25rem;
}

.wallet-insufficient {
  font-size: 0.6rem;
  font-weight: 500;
  color: #dc2626;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .payment-method-btn {
    padding: 0.75rem 0.25rem;
  }

  .method-icon {
    font-size: 1.25rem;
  }

  .method-name {
    font-size: 0.65rem;
  }
}
</style>
